import {
  BetSlipMode,
  EventStatus,
  LocalStorageKeys,
  OddMatchConditionEnum,
  OddStatus,
} from "../../types/enums";

export const createInitialBetSlipState = (
  betSlipState: Partial<IBetSlipState>,
  config: IWidgetConfig,
): IBetSlipState => {
  return {
    version: "single-",
    betBuilderState: {
      bets: [],
      totalOdds: [],
      __version: 0,
      config: {
        calculationApiUrl: config.betBuilder.betBuilderCalculationApiUrl,
        brandId: config.brand.id,
        viewOnly: false,
        allowRemoveEvent: true,
        calculationResultBottom: false,
        showTicketLinessStatus: false,
        showTicketEventStatus: false,
        acceptOddsChange: betSlipState.acceptOddsChanges,
        culture: config.culture,
        selectionNumber: config.betBuilder.selectionNumber,
      },
    },
    mode: betSlipState?.mode || BetSlipMode.Single,
    selections: betSlipState?.selections || [],
    acceptOddsChanges:
      betSlipState?.acceptOddsChanges ??
      config.brand.autoAcceptOddsChanges ??
      false,
    source: betSlipState?.source ?? null,
    sourceRef: betSlipState?.sourceRef ?? null,
  };
};

export const isSelectionExpired = (s: {
  gameTime: string;
  isLive: boolean;
  eventStatus?: number;
  betStatus?: number;
}) => {
  return new Date(s.gameTime).getTime() <= Date.now() && !s.isLive;
};

export const isBetBuilderBetExpired = (bet: IBetBuilderBet): boolean =>
  !!bet.date && new Date(bet.date).getTime() <= Date.now();

const findMatchingOdd = (
  selection: IBetSlipSelection,
  betType?: IBetType,
): IOdd | undefined => {
  if (!betType?.odds?.length) return undefined;

  let expectedLine = selection.oddLine;
  if (!expectedLine && selection.key) {
    const lineMatch = selection.key.match(/L([\d.]+)/);
    if (lineMatch) {
      expectedLine = lineMatch[1];
    }
  }

  return betType.odds.find((odd: IOdd) => {
    // Match by name or ID — oddName stores selected.id, not always n
    const oddNameMatches =
      odd.n === selection.oddName || odd.id === selection.oddName;
    const lineMatches = expectedLine
      ? odd.l === expectedLine
      : !odd.l || !expectedLine;
    return oddNameMatches && lineMatches;
  });
};

// Classifies an odd update into a fixed set of cases so callers can `switch`
// on it instead of re-deriving the same matched/suspended/closed branches.
// Feeds commonly drop an odd from the payload once it's suspended/closed, so
// "unmatched" still needs to resolve from the bet-type/event status.
export const findOddMatchCondition = ({
  matchingOddStatus,
  betTypeStatus,
  eventStatus,
}: {
  matchingOddStatus?: number;
  betTypeStatus?: number;
  eventStatus: number;
}): OddMatchConditionEnum => {
  const isMatched = matchingOddStatus !== undefined;
  const isClosed =
    matchingOddStatus === OddStatus.Closed ||
    betTypeStatus === OddStatus.Closed ||
    eventStatus === EventStatus.Closed; // a closed event closes every market within it
  const isSuspended =
    matchingOddStatus === OddStatus.Suspended ||
    betTypeStatus === OddStatus.Suspended; // no such thing as an event-level suspension

  if (isMatched && isClosed) return OddMatchConditionEnum.MatchedClosed;
  if (isMatched && isSuspended) return OddMatchConditionEnum.MatchedSuspended;
  if (isMatched) return OddMatchConditionEnum.MatchedActive;
  if (isClosed) return OddMatchConditionEnum.UnmatchedClosed;
  if (isSuspended) return OddMatchConditionEnum.UnmatchedSuspended;
  return OddMatchConditionEnum.Unchanged;
};

export const resolveSelectionOddUpdate = (
  selection: IBetSlipSelection,
  betType: IBetType | undefined,
  eventStatus: number,
): { betStatus: number; oddPrice: number; oldOddPrice: number } => {
  const matchingOdd = findMatchingOdd(selection, betType);
  const condition = findOddMatchCondition({
    matchingOddStatus: matchingOdd?.s,
    betTypeStatus: betType?.s,
    eventStatus,
  });

  const oddPrice = matchingOdd
    ? parseFloat(matchingOdd.p)
    : selection.oddPrice;
  const priceChanged = !!matchingOdd && oddPrice !== selection.oddPrice;

  let betStatus = selection.betStatus;
  switch (condition) {
    case OddMatchConditionEnum.MatchedClosed:
    case OddMatchConditionEnum.UnmatchedClosed:
      betStatus = OddStatus.Closed;
      break;
    case OddMatchConditionEnum.MatchedSuspended:
    case OddMatchConditionEnum.UnmatchedSuspended:
      betStatus = OddStatus.Suspended;
      break;
    case OddMatchConditionEnum.MatchedActive:
      betStatus = OddStatus.Active;
      break;
    case OddMatchConditionEnum.Unchanged:
    default:
      break;
  }

  return {
    betStatus,
    oddPrice,
    oldOddPrice: priceChanged ? selection.oddPrice : selection.oldOddPrice,
  };
};

const resolveEventStatus = (rawStatus: number): EventStatus => {
  switch (rawStatus) {
    case EventStatus.Live:
      return EventStatus.Live;
    case EventStatus.Closed:
      return EventStatus.Closed;
    case EventStatus.Prematch:
    default:
      return EventStatus.Prematch;
  }
};

const warnOnStatusChange = (
  selection: IBetSlipSelection,
  betStatus: number,
): void => {
  if (betStatus === OddStatus.Active || selection.betStatus !== OddStatus.Active) {
    return;
  }
  console.warn(
    `Selection "${selection.homeName} - ${selection.awayName}" status changed from Active to ${
      betStatus === OddStatus.Suspended ? "Suspended" : "Closed"
    }`,
  );
};

// Applies a single incoming event's data to one selection: resolves the event
// status (and isLive), then the odd/bet-type status and price via the bet
// type lookup — the two things updateOddsChanges needs per selection.
export const resolveUpdatedSelection = (
  selection: IBetSlipSelection,
  updatedEvent: IEvent,
): IBetSlipSelection => {
  const updatedSelection: IBetSlipSelection = { ...selection, event: updatedEvent };

  const rawEventStatus =
    updatedEvent.st ?? (updatedEvent as any).s ?? EventStatus.Prematch;
  const eventStatus = resolveEventStatus(rawEventStatus);
  updatedSelection.eventStatus = eventStatus;
  updatedSelection.isLive = eventStatus === EventStatus.Live;

  if (eventStatus === EventStatus.Closed) {
    // The event itself closing closes every market in it — don't depend on
    // the feed still listing bet types/odds for an event that's now closed.
    warnOnStatusChange(selection, OddStatus.Closed);
    updatedSelection.betStatus = OddStatus.Closed;
    return updatedSelection;
  }

  const betType = updatedEvent.bts?.find((bt) => bt.id === selection.betTypeId);
  if (betType) {
    const { betStatus, oddPrice, oldOddPrice } = resolveSelectionOddUpdate(
      selection,
      betType,
      eventStatus,
    );
    warnOnStatusChange(selection, betStatus);
    updatedSelection.betStatus = betStatus;
    updatedSelection.oddPrice = oddPrice;
    updatedSelection.oldOddPrice = oldOddPrice;
  }

  return updatedSelection;
};

const findRecoveryChange = (
  selection: IBetSlipSelection,
  changes: RecoveryChange[],
): RecoveryChange | undefined =>
  changes.find((change) => {
    const eventId = change.ev_id ?? change.ev_Id;
    if (eventId !== selection.eventId) return false;
    return change.bt_id != null ? change.bt_id === selection.betTypeId : true;
  });

const resolveRecoveryBetStatus = (
  oddStatus: number,
  eventStatus: EventStatus,
): OddStatus => {
  if (oddStatus === OddStatus.Closed || eventStatus === EventStatus.Closed) {
    return OddStatus.Closed;
  }
  if (oddStatus === OddStatus.Suspended) {
    return OddStatus.Suspended;
  }
  return OddStatus.Active;
};

// additionalInfo recovery (from placebetsport/executeOrder) carries its own
// compact change format — separate from the live odds feed IEvent shape —
// so it gets its own small resolver rather than reusing resolveUpdatedSelection.
export const applyRecoveryChange = (
  selection: IBetSlipSelection,
  changes: RecoveryChange[],
): IBetSlipSelection => {
  const change = findRecoveryChange(selection, changes);
  if (!change) return selection;

  const newPrice = parseFloat(String(change.o_p ?? selection.oddPrice));
  const priceChanged = newPrice !== selection.oddPrice;
  const eventStatus: EventStatus = change.ev_s ?? selection.eventStatus;
  const oddStatus = change.o_s ?? OddStatus.Active;

  return {
    ...selection,
    oddPrice: newPrice,
    oldOddPrice: priceChanged ? selection.oddPrice : selection.oldOddPrice,
    eventStatus,
    betStatus: resolveRecoveryBetStatus(oddStatus, eventStatus),
  };
};

export const createInitialWidgetState = (
  config: IWidgetConfig,
  betSlipState: Partial<IBetSlipState>,
  id: string = "",
): IWidgetState => {
  const initialBetSlipState = createInitialBetSlipState(betSlipState, config);

  return {
    config,
    betSlipState: initialBetSlipState,
    error: undefined,
    id,
  };
};

export const getInitialWidgetState = (
  props: Partial<IWidgetState> & {
    config: IWidgetConfig;
    betSlipState: Partial<IBetSlipState>;
  },
): IWidgetState => {
  const { config, error, id, betSlipState } = props;

  try {
    const savedState = localStorage.getItem(LocalStorageKeys.BET_SLIP_WIDGET);
    if (savedState) {
      const parsedState: IWidgetState = JSON.parse(savedState);

      // Filter out expired selections from saved state
      const activeSelections = parsedState.betSlipState.selections.filter(
        (selection: IBetSlipSelection) => !isSelectionExpired(selection),
      );

      // Merge configs and preserve saved bet slip state
      const mergedState: IWidgetState = {
        config: { ...parsedState.config, ...config },
        betSlipState: {
          ...parsedState.betSlipState,
          betBuilderState: {
            ...parsedState.betSlipState.betBuilderState,
            bets: (parsedState.betSlipState.betBuilderState.bets || []).filter(
              (bet: IBetBuilderBet) => !isBetBuilderBetExpired(bet),
            ),
            config: {
              ...parsedState.betSlipState.betBuilderState.config,
              selectionNumber: config.betBuilder.selectionNumber,
            },
          },
          selections: activeSelections,
          // Keep existing acceptOddsChanges from saved state
          acceptOddsChanges: parsedState.betSlipState.acceptOddsChanges,
          // Set mode based on active selections length if selections exist
          // Preserve saved mode, but validate it's still possible
          mode: (() => {
            const savedMode = parsedState.betSlipState.mode;
            const hasMultiple =
              activeSelections.length > 1 ||
              parsedState.betSlipState.betBuilderState?.bets?.length > 0;

            return savedMode
              ? savedMode
              : hasMultiple
                ? BetSlipMode.Combo
                : BetSlipMode.Single;
          })(),
        },
        error: error || undefined,
        id: id || parsedState.id || "",
      };

      localStorage.setItem(
        LocalStorageKeys.BET_SLIP_WIDGET,
        JSON.stringify(mergedState),
      );
      return mergedState;
    }
  } catch (error) {
    console.error("Error reading from localStorage:", error);
  }

  const newState: IWidgetState = createInitialWidgetState(
    config,
    betSlipState,
    id,
  );

  try {
    localStorage.setItem(
      LocalStorageKeys.BET_SLIP_WIDGET,
      JSON.stringify(newState),
    );
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }

  return newState;
};
